import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { showError } from '../../Swal';
import Loader from '../../../Loader';
import { HandleAPIError } from '../../../commonFunction';
import { login, userLogin } from '../../../slices/auth-slice';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { ILogin } from '../../../types';

const LoginUserForm = () => {
  const md5 = require('md5');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [displayPassword, setDisplayPassword] = useState(false);
  const loading = useAppSelector((state) => state.auth.loading);
  const userData = useAppSelector((state) => state.auth);

  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
  });

  const handleSubmit = async (values: any) => {
    const { username, password } = values;

    const userName = username.trim();
    const passWord = password.trim();

    try {
      const request: ILogin = {
        username: userName,
        password: md5(passWord),
      };

      const responseData = await dispatch(userLogin(request)).unwrap();

      if (responseData?.data?.userdefault?.themeeditorallowed === "N") {
        showError('ERROR', 'Theme Editor Access is not allowed for this user');
      };

      if (responseData?.status !== 0) {
        showError('ERROR', responseData?.statusMessage || "Login unsuccessful")
      }

    } catch (error) {
      HandleAPIError(error);
    }
  };

  useEffect(() => {
    if (userData.publisher === true) {
      navigate('/publisher-dashboard', { replace: true });
    } else if (userData.editor === true) {
      navigate('/editor-dashboard', { replace: true });
    } else if (userData.user === true) {
      navigate('/user-dashboard', { replace: true });
    };
  }, [userData, navigate]);

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const togglePassword = () => {
    setDisplayPassword(!displayPassword);
  };

  return (
    <>
      <Loader loading={loading} />
      <section className='login-bg-color'>
        <Container className="my-4" >
          <Row className="d-flex justify-content-center min-vh-100 align-items-center">
            <Col lg={4}>
              <Card className="bg-logincart card bg-opacity-10 rounded-3 p-2 my-3 shadow-lg">
                <Card.Body className='bg-logincart p-4 rounded-3'>
                  <h2 className="d-flex justify-content-center">USER LOGIN</h2>
                  <Card.Text className="d-flex justify-content-center">
                    Please login with your information below
                  </Card.Text>
                  <hr className="horizontalBar" />
                  <Row className="d-flex justify-content-start">
                    <Col md={12}>
                      <h6 className="text-red mb-2">Fill all the fields marked with *</h6>
                    </Col>
                  </Row>
                  {((formik.touched.username && formik.errors.username) ||
                    (formik.touched.password && formik.errors.password)) && (
                      <div
                        className="alert alert-danger py-2 px-3 mb-1"
                        style={{ backgroundColor: "#ffebee", border: "none", borderRadius: "4px" }}
                      >
                        <h5 className="mb-1" style={{ color: "#d32f2f" }}>
                          Please resolve the following error to continue.
                        </h5>
                        {formik.touched.username && formik.errors.username && (
                          <p className="m-1" style={{ color: "#d32f2f" }}>
                            {formik.errors.username}
                          </p>
                        )}
                        {formik.touched.password && formik.errors.password && (
                          <p className="m-1" style={{ color: "#d32f2f" }}>
                            {formik.errors.password}
                          </p>
                        )}
                      </div>
                    )}
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={12} className="mb-3 mt-3">
                        <Form.Label htmlFor="validationCustomUsername" className="">
                          Username
                          <span className="text-red">*</span>
                        </Form.Label>
                        <InputGroup className={formik.touched.username && formik.errors.username ? 'error d-flex' : 'd-flex'}>
                          <Form.Control
                            type="text"
                            id="validationCustomUsername"
                            placeholder="Username"
                            aria-describedby="inputGroupPrepend"
                            name="username"
                            value={formik.values.username}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={12} className="mb-3">
                        <Form.Label htmlFor="validationCustomPassword" className="mb-1">
                          Password<span className="text-red">*</span>
                        </Form.Label>
                        <InputGroup className={formik.touched.password && formik.errors.password ? 'error d-flex' : ''}>
                          <Form.Control
                            type={displayPassword ? "text" : "password"}
                            placeholder="Password"
                            name="password"
                            id="validationCustomPassword"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            style={{
                              borderRadius: "6px",
                            }}
                          />
                          <FontAwesomeIcon
                            icon={displayPassword ? faEye : faEyeSlash}
                            size="sm"
                            className="icon-color"
                            onClick={togglePassword}
                            style={{
                              position: 'absolute',
                              top: 11,
                              right: 10,
                              cursor: 'pointer',
                              zIndex: '5',
                            }}
                          />
                        </InputGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12} className="text-center">
                        <Button
                          type="submit"
                          className="btn-success login-font-w login-btn shadow-sm"
                        >
                          LOGIN
                        </Button>
                      </Col>
                    </Row>

                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default LoginUserForm;
